<script lang="ts">
  import { fn } from "../../../stores";
  import { Pages } from "../../../utils/enums";
  import type { TPaymentReturn } from "../../../utils/types";
  import { get } from "svelte/store";
  import { getTxUrl } from "./service";
  export let options: {
    title: string;
    isSui?: boolean;
    description: string | undefined;
    subtitle: string;
    orders: TPaymentReturn["items"];
    networkType?: string;
    tokenImage?: string;
  };
  const { openUI } = get(fn);
  let {
    title,
    subtitle,
    orders,
    networkType,
    tokenImage,
    isSui,
  } = options;

  const successfulOrders = (orders ?? []).filter((o) => o.tx);
  const failedOrders = (orders ?? []).filter((o) => !o.tx);

  function retryPurchase() {
    const hasAssets = failedOrders.some((order) => order.asset);
    if (hasAssets) {
      const assets = failedOrders
        .filter((order) => order.asset)
        .map((order) => ({
          packageId: order.asset!.packageId,
          type: order.asset!.type,
          assetId: order.asset!.assetId,
          amount: order.asset!.amount,
        }));
      if (!assets.length) return;

      openUI(Pages.PAYMENT, {
        subtitle: "Retrying failed orders",
        payments: {
          credenzaStoredValue: {
            disabled: false,
          },
        },
        assets,
      });

      return;
    }
    const tokens: {
      contractAddress: string;
      tokenId: number;
      amount: number;
    }[] = [];

    const memberships: {
      contractAddress: string;
    }[] = [];

    for (const order of failedOrders) {
      const directive = order.directive;
      if (directive.type !== "membership") {
        tokens.push({
          contractAddress: directive.resource_id,
          tokenId: directive.value?.token_id,
          amount: directive.value.amount,
        });
      } else {
         memberships.push({
          contractAddress: directive.resource_id,
        });
      }
    }
  
    if (!tokens.length && !memberships.length) return;
    openUI(Pages.PAYMENT, {
      subtitle: "Retrying failed orders",
      payments: {
        credenzaStoredValue: {
          disabled: false,
        },
      },
      tokens,
      memberships,
    });
  }
</script>

<div>
  <div
    class="crtw-text-black-900 crtw-text-xl md:crtw-text-2xl crtw-font-semibold crtw-pt-5 crtw-mb-3.5 crtw-text-center"
  >
    {title}
  </div>
  <div class="crtw-mb-3.5 crtw-text-sm crtw-text-justify">
    {subtitle}
  </div>
  {#if tokenImage}
    <div class="crtw-my-4 flex justify-center">
      <div
        class="crtw-w-64 crtw-h-64 crtw-rounded-xl crtw-shadow-md crtw-bg-white flex items-center justify-center overflow-hidden"
      >
        <img
          src={tokenImage}
          alt="Token"
          class="max-w-full max-h-full object-contain"
        />
      </div>
    </div>
  {/if}

  <div class="crtw-mb-3.5 crtw-text-sm crtw-text-justify">
    {#if orders.length}
      <div class="crtw-mt-6">
        <div class="crtw-text-lg crtw-font-semibold crtw-mb-2">Successful</div>
        {#each successfulOrders as order}
          {#if order.tx}
            <div class="crtw-mb-2">
              <a
                href={getTxUrl(
                  { chainId: order.directive?.chain_id || "" },
                  order.tx,
                  order.directive.chain_type,
                  networkType || ""
                )}
                target="_blank"
                rel="noopener noreferrer"
                class="crtw-text-blue-600 crtw-underline"
              >
                <div class="crtw-break-words">
                  {#if order.metadata?.name}
                    {order.metadata.name}
                  {:else}
                    <span>{order.directive.type}</span>
                    <span
                      class="crtw-break-all crtw-whitespace-normal crtw-break-words"
                    >
                      ({order.directive.resource_id})
                    </span>
                  {/if}
                </div>
              </a>
            </div>
          {/if}
        {/each}

        <div
          class="crtw-text-lg crtw-font-semibold crtw-mt-6 crtw-mb-2"
          class:hidden={!failedOrders.length}
        >
          Failed
        </div>
        {#if failedOrders.length}
          {#each failedOrders as order}
            <div class="crtw-mb-3">
              <p class="crtw-text-red-600 break-words">
                {#if order.metadata?.name}
                  {order.metadata.name}
                {:else if order.directive?.type}
                  <span>{order.directive.type} </span>
                  <span class="text-gray-500 break-all whitespace-normal">
                    ({order.directive.resource_id})
                  </span>
                {:else if order.asset?.assetId}
                  AssetId {order.asset.assetId} : {order.error}
                {:else}
                  <span>{order.asset?.type}</span>
                  <span
                    class="crtw-break-all crtw-whitespace-normal crtw-break-words"
                  >
                    ({order.asset?.packageId}) : {order.error}
                  </span>
                {/if}
              </p>
            </div>
          {/each}
        {/if}
        <button
          class="crtw-bg-black hover:crtw-bg-gray-800 crtw-text-white crtw-px-4 crtw-py-2 crtw-rounded-md crtw-mt-4"
          on:click={retryPurchase}
        >
          Try Again
        </button>
      </div>
    {:else if isSui}
      If you are not seeing your new token, please contact
      <a href="mailto:support@credenza3.com" class="text-blue-600 underline">
        support@credenza3.com
      </a>.
    {:else}
      Typically, your purchase will be processed within a few seconds. In the
      event of unusually high traffic, you may have to wait as much as a minute
      or so. If several minutes pass and you are not seeing your purchase,
      please contact
      <a class="crtw-underline" href="mailto:support@credenza3.com"
        >support@credenza3.com</a
      >.
    {/if}
  </div>
</div>
